(() => {
  const prefetchedUrls = new Set();
  const maxPrefetches = 8;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection?.saveData || /2g/.test(connection?.effectiveType || "")) return;

  // The document a link loads: its URL without the #fragment. A link to a section of another page
  // ("/pages/...#signup", the CYBERFEST menu's Join Early Access) loads that page, so it is prefetched like any
  // other link; it used to be skipped, which made it the one menu link that opened slowly after a tap on phones
  // (QA 4 Nov: "first tap does not open the target page").
  const documentUrl = (link) => {
    try {
      const url = new URL(link.href, window.location.href);
      url.hash = "";
      return url;
    } catch (_) {
      return null;
    }
  };

  const shouldPrefetch = (link) => {
    if (!link || prefetchedUrls.size >= maxPrefetches) return false;
    if (link.target && link.target !== "_self") return false;
    if (link.hasAttribute("download") || link.dataset.noPrefetch === "true") return false;

    const url = documentUrl(link);
    if (!url) return false;

    if (url.origin !== window.location.origin) return false;
    // The page the shopper is on (a #section link on it is a jump, not a visit).
    if (url.href === documentUrl({ href: window.location.href })?.href) return false;
    if (prefetchedUrls.has(url.href)) return false;
    if (/\/(account|cart|checkout|search)(\/|$)/.test(url.pathname)) return false;

    return true;
  };

  const prefetch = (link) => {
    if (!shouldPrefetch(link)) return;

    const url = documentUrl(link);
    const hint = document.createElement("link");
    hint.rel = "prefetch";
    hint.as = "document";
    hint.href = url.href;
    document.head.appendChild(hint);
    prefetchedUrls.add(url.href);
  };

  const prefetchFromEvent = (event) => {
    const link = event.target?.closest?.("a[href]");
    prefetch(link);
  };

  /* A mouse sweeping across the page used to spend the 8 prefetches (each a
     full page render) on links it only crossed. A mouse now has to rest on a
     link briefly, or press it; pen hover and keyboard focus prefetch at once.
     Touch has its own path below. */
  let pendingLink = null;
  let hoverTimer = null;

  const prefetchOnHover = (event) => {
    // A finger fires pointerenter at the start of every scroll too; touch waits for a tap (below).
    if (event.pointerType === "touch") return;
    const link = event.target?.closest?.("a[href]");
    if (!link) return;
    if (event.pointerType !== "mouse") {
      prefetch(link);
      return;
    }
    // pointerenter also fires for each child of a card link; keep that link's timer running.
    if (pendingLink === link) return;
    clearTimeout(hoverTimer);
    pendingLink = link;
    hoverTimer = setTimeout(() => {
      pendingLink = null;
      prefetch(link);
    }, 65);
  };

  const cancelHover = (event) => {
    if (event.target !== pendingLink) return;
    clearTimeout(hoverTimer);
    pendingLink = null;
  };

  const prefetchOnPress = (event) => {
    if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;
    prefetchFromEvent(event);
  };

  /* Touch: a swipe that starts on a card is a scroll, not a visit, and used to
     prefetch that page. A touch on a link now prefetches only when it looks like
     a tap: the finger lifts, or rests for 100 ms, without moving more than 10 px
     and without the page scrolling. */
  const TOUCH_SLOP = 10;
  let touchLink = null;
  let touchTimer = null;
  let touchX = 0;
  let touchY = 0;

  const cancelTouch = () => {
    clearTimeout(touchTimer);
    touchLink = null;
  };

  const prefetchTouch = () => {
    const link = touchLink;
    cancelTouch();
    prefetch(link);
  };

  const onTouchStart = (event) => {
    cancelTouch();
    if (event.touches.length !== 1) return;
    const link = event.target?.closest?.("a[href]");
    if (!link) return;
    touchLink = link;
    touchX = event.touches[0].clientX;
    touchY = event.touches[0].clientY;
    touchTimer = setTimeout(prefetchTouch, 100);
  };

  const onTouchMove = (event) => {
    if (!touchLink) return;
    const touch = event.touches[0];
    if (!touch || Math.abs(touch.clientX - touchX) > TOUCH_SLOP || Math.abs(touch.clientY - touchY) > TOUCH_SLOP) {
      cancelTouch();
    }
  };

  const onTouchEnd = () => {
    if (touchLink) prefetchTouch();
  };

  const onScroll = () => {
    if (touchLink) cancelTouch();
  };

  document.addEventListener("pointerenter", prefetchOnHover, { capture: true, passive: true });
  document.addEventListener("pointerleave", cancelHover, { capture: true, passive: true });
  document.addEventListener("pointerdown", prefetchOnPress, { capture: true, passive: true });
  document.addEventListener("focusin", prefetchFromEvent, { passive: true });
  document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
  document.addEventListener("touchmove", onTouchMove, { capture: true, passive: true });
  document.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
  document.addEventListener("touchcancel", cancelTouch, { capture: true, passive: true });
  // Capture also sees scrolls of inner scrollers (carousels), which do not bubble.
  document.addEventListener("scroll", onScroll, { capture: true, passive: true });
})();
